import { BannerTrackingConfig } from "../hook/useBannerTracking"
import { ApiResponse, BannerData, BannerOptions, SelectOption, WebsiteData } from "../types/types"

export const processWebsiteOptions = (websitesResponse: ApiResponse<WebsiteData>): SelectOption[] => {
  if (!websitesResponse?.data) {
    return []
  }

  if (!Array.isArray(websitesResponse.data)) {
    return []
  }

  const processed = websitesResponse.data
    .filter((website) => {
      const hasName = Boolean(website?.attributes?.name)
      const hasDomain = Boolean(website?.attributes?.domain)
      const hasId = Boolean(website?.id)

      if (!hasName || !hasDomain || !hasId) {
        return false
      }

      return true
    })
    .map((website) => ({
      value: website.id,
      label: `${website.attributes.name} (${website.attributes.domain})`
    }))

  return processed
}

export const extractUniquePositions = (bannersResponse: ApiResponse<BannerData>): SelectOption[] => {
  if (!bannersResponse?.data) {
    return []
  }

  if (!Array.isArray(bannersResponse.data)) {
    return []
  }

  const positions = bannersResponse.data
    .map((banner) => banner?.attributes?.position)
    .filter((pos): pos is string => Boolean(pos))

  const uniquePositions = Array.from(new Set<string>(positions))

  const result = uniquePositions.map((position) => ({
    value: position,
    label: position.charAt(0).toUpperCase() + position.slice(1)
  }))
  return result
}

export const getDefaultTargetOptions = (): SelectOption[] => [
  { value: "_blank", label: "New Tab" },
  { value: "_self", label: "Same Tab" },
  { value: "_parent", label: "Parent Frame" },
  { value: "_top", label: "Top Frame" },
]

export const getFallbackBannerOptions = (): BannerOptions => ({
  pageOptions: [
    { value: "HOME", label: "Home" },
    { value: "NEWS", label: "News" },
    { value: "VIDEOS", label: "Videos" },
    { value: "CATEGORY", label: "Category" },
    { value: "NEWS_DETAIL", label: "News Detail" },
    { value: "STATIC_PAGE", label: "Static Page" },
    { value: "EVERY_PAGES", label: "Every Pages" },
  ],
  positionOptions: [
    { value: "ABOVE_HEADER", label: "ABOVE_HEADER" },
    { value: "TOP_CONTENT", label: "TOP_CONTENT" },
    { value: "SIDEBAR", label: "SIDEBAR" },
    { value: "CONTENT", label: "CONTENT" },
    { value: "FOOTER", label: "FOOTER" },
    { value: "STICKY_SIDEBAR_LEFT", label: "STICKY_SIDEBAR_LEFT" },
    { value: "STICKY_SIDEBAR_RIGHT", label: "STICKY_SIDEBAR_RIGHT" },
    { value: "OTHER", label: "OTHER" },
    { value: "HOME_SECTION", label: "HOME_SECTION" },
  ],
  websiteOptions: [],
  targetOptions: getDefaultTargetOptions()
})

export const processBannerData = (banners: BannerData[]) => {
  return banners.map((banner) => ({
    ...banner,
    status: banner.attributes.publishedAt ? "published" : "draft",
    publishedAt: banner.attributes.publishedAt,
  }))
}

export const ensureBannerDataIntegrity = (bannerData: BannerData) => {
  if (bannerData && bannerData.attributes) {
    if (bannerData.attributes.isActive === undefined) {
      bannerData.attributes.isActive = false
    }
  }
  return bannerData
}

export const extractBannerTrackingConfig = (banner: BannerData): BannerTrackingConfig | undefined => {
  const { attributes } = banner

  if (!attributes.common) return undefined

  return {
    clickUrl: attributes.common.clickUrl,
    impressionHtml: attributes.common.impressionHtml,
    vendorScriptSrc: attributes.common.vendorScriptSrc,
    vendorNoscriptHtml: attributes.common.vendorNoscriptHtml,
  }
}

export const transformBannerWithTracking = (banner: BannerData) => {
  const { attributes } = banner

  return {
    id: banner.id,
    name: attributes.name,
    url: attributes.url,
    target: attributes.target,
    image: attributes.banner?.data?.attributes?.url ||
           attributes.mobileBanner?.data?.attributes?.url || "",
    trackingConfig: extractBannerTrackingConfig(banner),
  }
}

